import { Box, Grid, useTheme } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { t as translate } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { IAuthUser } from 'auth/types';
import { BomPartType } from 'bom/enums';
import { Button } from 'core/components';
import { VersionTypeHelper } from 'core/helpers';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { canOrderStillBeSubmitted } from 'quotes/components/CustomerQuotePartList/quoteStatus';
import { SubmitCustomerOrder } from 'quotes/components/SubmitCustomerOrder';
import { submitOrderBlockTooltipKeyMap } from 'quotes/components/SubmitCustomerOrder/constants';
import { getSubmitOrderBlockReason } from 'quotes/components/SubmitCustomerOrder/utils';
import { QuoteEntityType } from 'quotes/enums';
import {
  ICustomerQuote,
  ICustomerQuotePart,
  IProjectSubmitBlock,
  IProjectSubmitWarning,
  IQuotePart,
} from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';
import { Color, Size, TooltipPosition, Variant } from 'theme/enums';

import { SubmitCustomerOrderConfirmation } from './SubmitCustomerOrderConfirmation';

interface IVersionMismatchTooltipProps {
  quote: ICustomerQuote;
}

const VersionMismatchTooltipTitle = ({ quote }: IVersionMismatchTooltipProps) => {
  const joyTheme = useTheme();

  return (
    <Grid container>
      <Grid
        xs={2}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Warning
          style={{
            width: 40,
            height: 40,
            fill: joyTheme.palette.warning[400],
          }}
        />
      </Grid>
      <Grid
        xs={10}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 0',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Trans
            i18nKey="messageSubmitOrderNotAllowedDifferentVersion"
            values={{ versionType: VersionTypeHelper.getVersionTypeLabelByQuote(quote) }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

const getSubmitOrderTooltipProps = (
  quote: ICustomerQuote,
  user: IAuthUser,
  projectSubmitBlockMessage: string | null
) => {
  const isVersionMismatch =
    Boolean(quote?.alternateVersionEnabled) !== Boolean(user?.alternateVersionEnabled);

  if (isVersionMismatch) {
    return {
      placement: TooltipPosition.TOP_START,
      color: Color.WARNING,
      size: Size.LG,
      title: <VersionMismatchTooltipTitle quote={quote} />,
      children: null,
      sx: { minWidth: 400 },
    };
  }

  const blockReason = getSubmitOrderBlockReason(quote);

  if (!blockReason && projectSubmitBlockMessage) {
    return {
      placement: TooltipPosition.TOP_START,
      color: Color.DANGER,
      size: Size.LG,
      title: projectSubmitBlockMessage,
      children: null,
      sx: { maxWidth: 400 },
    };
  }

  if (!blockReason) {
    return null;
  }

  return {
    placement: TooltipPosition.TOP_START,
    color: Color.WARNING,
    size: Size.LG,
    title: translate(submitOrderBlockTooltipKeyMap[blockReason], {
      externalOrderNumber: quote.externalOrderNumber,
    }),
    children: null,
  };
};

interface IProps {
  quote: ICustomerQuote;
  onUpdateQuote: (quote: ICustomerQuote) => void;
  quoteEntityType: QuoteEntityType;
  onSubmitSuccess?: (updatedQuote?: ICustomerQuote) => void;
  projectSubmitBlock?: IProjectSubmitBlock | null;
  projectSubmitWarning?: IProjectSubmitWarning | null;
}

export const SubmitCustomerOrderButton = ({
  quote,
  onUpdateQuote,
  quoteEntityType,
  onSubmitSuccess,
  projectSubmitBlock = null,
  projectSubmitWarning = null,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);

  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { rows, isDataLoadCompleted } = useAgGridClientTableContext();
  const { isLoadingTopNotificationDisplayed } = useNotificationContext();

  const activeParts = useMemo(
    () => getActiveQuoteParts(rows as IQuotePart[]) as ICustomerQuotePart[],
    [rows]
  );

  const isAlternateVersionEnabled = Boolean(quote.alternateVersionEnabled);

  const isVersionMismatch = isAlternateVersionEnabled !== Boolean(user?.alternateVersionEnabled);

  const isDisabled = isVersionMismatch || !isDataLoadCompleted || isLoadingTopNotificationDisplayed;

  // The BE refuses a blocked submit too; disabling here only spares the user the round trip.
  const projectSubmitBlockMessage =
    canOrderStillBeSubmitted(quoteEntityType, quote) && projectSubmitBlock?.message
      ? projectSubmitBlock.message
      : null;

  const isSubmitOrderEnabled =
    !isDisabled &&
    !projectSubmitBlockMessage &&
    !getSubmitOrderBlockReason(quote) &&
    activeParts?.some(
      (part: ICustomerQuotePart) =>
        BomPartType.PART === part.type &&
        !part.deleted &&
        (!isAlternateVersionEnabled || part.partArr?.manufacturerId)
    );

  const onConfirmValidation = useCallback(() => {
    setIsConfirmationOpen(false);
    setIsSubmitOpen(true);
  }, []);

  const onCancelConfirmation = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  const onCloseSubmit = () => {
    setIsSubmitOpen(false);
  };

  return (
    <>
      <Button
        text={t('buttonLabelSubmitOrder')}
        variant={!isSubmitOrderEnabled ? Variant.OUTLINED : Variant.SOLID}
        color={Color.SUCCESS}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={!isSubmitOrderEnabled}
        tooltipProps={getSubmitOrderTooltipProps(quote, user, projectSubmitBlockMessage)}
        sx={
          !isSubmitOrderEnabled
            ? {
                pointerEvents: 'auto !important',
                cursor: 'not-allowed !important',
                '&:hover': { backgroundColor: 'transparent !important' },
              }
            : undefined
        }
      />

      {isConfirmationOpen && (
        <SubmitCustomerOrderConfirmation
          quote={quote}
          onUpdateQuote={onUpdateQuote}
          quoteEntityType={quoteEntityType}
          parts={activeParts}
          onConfirm={onConfirmValidation}
          onCancel={onCancelConfirmation}
        />
      )}

      <SubmitCustomerOrder
        isOpen={isSubmitOpen}
        onClose={onCloseSubmit}
        quote={quote}
        projectSubmitWarning={projectSubmitWarning}
        onSubmitSuccess={onSubmitSuccess}
      />
    </>
  );
};
